import { Alert, FlexContainer } from '@act/storybook';
import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { BulkTransferConfirmationSummary } from './buildBulkTransferConfirmationSummary.util';

interface BulkMemberTransferConfirmationStepProps {
  summary: BulkTransferConfirmationSummary;
}

/**
 * Displays the selected members and transfer details before submission.
 *
 * @param props - Resolved transfer summary.
 * @returns Read-only confirmation content.
 */
export function BulkMemberTransferConfirmationStep({
  summary,
}: BulkMemberTransferConfirmationStepProps) {
  const detailRows = useMemo(() => {
    const rows = [
      { label: 'From guide', value: summary.originatingGuideName },
      { label: 'Reason', value: summary.reasonText },
    ];
    if (summary.effectiveDateLabel) {
      rows.push({
        label: 'Effective date',
        value: summary.effectiveDateLabel,
      });
    }
    if (summary.stringDetail) {
      rows.push({ label: 'Details', value: summary.stringDetail });
    }
    if (summary.addressDetails) {
      rows.push({ label: 'Addresses', value: summary.addressDetails });
    }
    rows.push({ label: 'Comment', value: summary.comment });
    return rows;
  }, [summary]);

  return (
    <FlexContainer direction="column" gap={2} height="auto" width="100%">
      <Typography variant="bodyLarge">You are going to transfer</Typography>
      <Alert severity="neutralLight" variant="tonal" hideIcon>
        {summary.memberNames.join('\n')}
      </Alert>
      <Typography variant="bodyLarge">
        to {summary.receivingGuideName}
      </Typography>
      <Alert severity="neutralLight" variant="tonal" hideIcon>
        <FlexContainer direction="column" gap={1.5} height="auto" width="100%">
          <Typography variant="labelLarge">Additional details</Typography>
          {detailRows.map(row => (
            <FlexContainer
              key={row.label}
              direction="column"
              gap={0.25}
              height="auto"
              width="100%"
            >
              <Typography variant="labelLarge">{row.label}</Typography>
              <Typography variant="bodyMedium">{row.value}</Typography>
            </FlexContainer>
          ))}
        </FlexContainer>
      </Alert>
    </FlexContainer>
  );
}
